import { splitEncoding } from '@contentful/live-preview';
import { CircularProgress } from '@mui/material';
import type { ResponsiveStyleValue } from '@mui/system';
import { type ButtonHTMLAttributes, useState } from 'react';

import HighlightLightboxVideoPlayer from '@/components/HighlightLightboxPlayer/HighlightLightboxVideoPlayer';
import Icon, { type IconName } from '@/components/Icon/Icon';
import Link from '@/components/Link/Link';
import { type Event, trackEvents } from '@/utils/analytics';

import useStyles from './Button.styles';

export enum ButtonVariant {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
  TERTIARY = 'Tertiary',
}

export enum ButtonSize {
  LG = 'Lg',
  MD = 'Md',
  SM = 'Sm',
  XS = 'Xs',
}

export enum ButtonIconPosition {
  LEFT = 'Left',
  RIGHT = 'Right',
}
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  // Custom Button Props
  onClick?: (e?: React.MouseEvent<HTMLElement>) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
  noPrefetchOnHover?: boolean;
  // Style props
  href?: string;
  isOnDark?: boolean;
  iconName?: IconName;
  iconPosition?: ButtonIconPosition;
  variant?: ButtonVariant;
  size?: ResponsiveStyleValue<ButtonSize>;
  isLoading?: boolean;
  // Analytics
  gaEvent?: Event;
  testId?: string;
  videoUrl?: string;
}

export default function Button({
  onClick,
  gaEvent,
  disabled,
  className,
  label,
  href,
  isOnDark = false,
  iconName,
  iconPosition = ButtonIconPosition.RIGHT,
  variant = ButtonVariant.PRIMARY,
  size = ButtonSize.MD,
  noPrefetchOnHover = false,
  isLoading = false,
  testId,
  videoUrl,
  ...props
}: ButtonProps) {
  const iconOnly = iconName && !label;
  const { classes, cx } = useStyles({
    iconOnly,
    isOnDark,
    size,
  });
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const togglePlayer = () => setIsPlayerOpen((prev) => !prev);

  function handleClick(
    event:
      | React.FormEvent<HTMLButtonElement>
      | React.FormEvent<HTMLAnchorElement>
  ) {
    if (gaEvent) {
      trackEvents(gaEvent);
    }
    if (videoUrl) {
      togglePlayer();
    }
    if (onClick) {
      event.preventDefault();
      onClick();
    }
  }

  const buttonClassName = cx(
    classes.button,
    classes.responsiveSizes,
    isOnDark ? 'dark' : 'light',
    classes[`icon${iconPosition}`],
    classes[variant],
    // biome-ignore lint/suspicious/noDoubleEquals: Automated migration to Biome
    variant == ButtonVariant.SECONDARY && classes.border,
    (disabled || isLoading) && classes.disabled,
    className
  );

  return onClick || videoUrl ? (
    <>
      <button
        onClick={handleClick}
        className={buttonClassName}
        disabled={disabled || isLoading}
        data-testid={testId}
        {...props}
      >
        {iconName && !isLoading && (
          <div className={classes.iconContainer}>
            <Icon name={iconName} />
          </div>
        )}
        {label && !isLoading && <p>{label}</p>}
        {isLoading && (
          <div className={classes.iconContainer}>
            <CircularProgress
              size={24}
              thickness={3}
              className={classes.loadingSpinner}
              aria-label='loading'
            />
          </div>
        )}
      </button>
      {videoUrl && (
        <HighlightLightboxVideoPlayer
          videoLink={videoUrl}
          isOpen={isPlayerOpen}
          onClose={() => setIsPlayerOpen(false)}
        />
      )}
    </>
  ) : (
    <Link
      href={href ? splitEncoding(href).cleaned : undefined}
      onClick={handleClick}
      className={buttonClassName}
      noPrefetchOnHover={noPrefetchOnHover}
    >
      {iconName && (
        <div className={classes.iconContainer}>
          <Icon name={iconName} />
        </div>
      )}
      {label && <p>{label}</p>}
    </Link>
  );
}
